import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import Money from "../../components/Money";
import { useHouseDispatch } from "../../contexts/HouseContext";
import { fetchSettlement } from "../../services/ledgerService";
import { MethodGlyph, paymentLabel } from "./components/paymentMethods";
import "./SettlementView.css";

/**
 * Table cash-out. Fetches the minimized "who pays whom" plan for the game and
 * shows it as a full-screen sheet so the host can coordinate the payouts
 * instead of working them out by hand. Tap a row to check it off as you send
 * the money (a session-only tracker); "Mark settled" closes the books — settles
 * every outstanding transaction. See docs/table-cashout-settlement.md.
 */
export function SettlementView({ game, onClose }) {
    const { settleGame } = useHouseDispatch();
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [paid, setPaid] = useState(() => new Set());
    const [submitting, setSubmitting] = useState(false);
    const [settleError, setSettleError] = useState(null);

    useEffect(() => {
        let active = true;
        fetchSettlement({ gameId: game.id })
            .then((data) => active && setState({ loading: false, error: null, data }))
            .catch((e) => active && setState({ loading: false, error: e.message, data: null }));
        return () => {
            active = false;
        };
    }, [game.id]);

    const { loading, error, data } = state;
    const transfers = data?.transfers ?? [];

    const togglePaid = (i) =>
        setPaid((prev) => {
            const next = new Set(prev);
            if (next.has(i)) next.delete(i);
            else next.add(i);
            return next;
        });

    const handleSettle = async () => {
        setSubmitting(true);
        setSettleError(null);
        const res = await settleGame(game.id);
        setSubmitting(false);
        if (res?.error) {
            setSettleError(res.error);
            return;
        }
        onClose();
    };

    const hasTransfers = !loading && !error && transfers.length > 0;

    return (
        <div className="sv" role="dialog" aria-label="Settle up">
            <header className="sv-head">
                <button type="button" className="sv-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
                <div className="sv-kicker">Settle up</div>
                <h1 className="sv-title">Who pays whom</h1>
                {hasTransfers && (
                    <div className="sv-sub">
                        {transfers.length} transfer{transfers.length === 1 ? "" : "s"} · minimized
                    </div>
                )}
            </header>

            <div className="sv-body">
                {loading && <p className="sv-note">Working out the settlement…</p>}
                {error && <div className="sv-error">{error}</div>}

                {!loading && !error && transfers.length === 0 && (
                    <p className="sv-note">Everyone&apos;s square — nothing to settle.</p>
                )}

                {hasTransfers && (
                    <>
                        <p className="sv-note sv-hint">Tap a payment once you&apos;ve sent it.</p>
                        <ul className="sv-list">
                            {transfers.map((t, i) => (
                                <li key={i}>
                                    <button
                                        type="button"
                                        className={`sv-row${paid.has(i) ? " is-paid" : ""}`}
                                        onClick={() => togglePaid(i)}
                                        aria-pressed={paid.has(i)}
                                    >
                                        <span className="sv-check" aria-hidden="true" />
                                        <span className="sv-names">
                                            <span className="sv-from">{t.from}</span>
                                            <span className="sv-arrow" aria-hidden="true">→</span>
                                            <span className="sv-to">{t.to}</span>
                                        </span>
                                        <span className="sv-method">
                                            <MethodGlyph type={t.method} aria-hidden="true" />
                                            {t.method === "CASH" ? "in person" : paymentLabel(t.method)}
                                        </span>
                                        <span className="sv-amt">
                                            <Money amount={t.amount} />
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {settleError && <div className="sv-error">{settleError}</div>}
                    </>
                )}
            </div>

            <footer className="sv-foot">
                {hasTransfers ? (
                    <button
                        type="button"
                        className="sv-btn"
                        onClick={handleSettle}
                        disabled={submitting}
                    >
                        {submitting ? "Settling…" : "Mark settled"}
                    </button>
                ) : (
                    <button type="button" className="sv-btn" onClick={onClose}>
                        Done
                    </button>
                )}
            </footer>
        </div>
    );
}

SettlementView.propTypes = {
    game: PropTypes.object.isRequired,
    onClose: PropTypes.func,
};
